import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { GeoProvider } from "./contexts/GeoContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CheckoutPage, { CheckoutSuccessPage } from "./pages/CheckoutPage";
import OrdersPage from "./pages/OrdersPage";
import WishlistPage from "./pages/WishlistPage";
import SizeGuidePage from "./pages/SizeGuidePage";
import ChangingRoomPage from "./pages/ChangingRoomPage";
import FootwearPage from "./pages/FootwearPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import ShannonChat from "./components/ShannonChat";
import { trackPageview } from "./lib/analytics";

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  // Fire an analytics pageview on every route/search change (fire-and-forget)
  useEffect(() => { trackPageview(pathname + search); }, [pathname, search]);
  return null;
}

// Layout wrapper for pages that need Header and Footer
function MainLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

// Auth pages don't need header/footer
function AuthLayout({ children }) {
  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <GeoProvider>
        <CartProvider>
          <WishlistProvider>
            <Toaster 
              position="top-right" 
              toastOptions={{
                style: {
                  border: '2px solid #0A0A0A',
                  borderRadius: '0',
                  fontFamily: "'Manrope', sans-serif",
                },
              }}
            />
            <Routes>
              {/* Auth Routes - No Header/Footer */}
              <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
              <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
              
              {/* Checkout Success - Minimal layout */}
              <Route path="/checkout/success" element={<CheckoutSuccessPage />} />

              {/* Main Routes - With Header/Footer */}
              <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
              <Route path="/products" element={<MainLayout><ProductsPage /></MainLayout>} />
              <Route path="/product/:id" element={<MainLayout><ProductDetailPage /></MainLayout>} />
              <Route path="/checkout" element={<MainLayout><CheckoutPage /></MainLayout>} />
              <Route path="/orders" element={<MainLayout><OrdersPage /></MainLayout>} />
              <Route path="/wishlist" element={<MainLayout><WishlistPage /></MainLayout>} />
              <Route path="/size-guide" element={<MainLayout><SizeGuidePage /></MainLayout>} />
              <Route path="/changing-room" element={<MainLayout><ChangingRoomPage /></MainLayout>} />
              <Route path="/footwear" element={<MainLayout><FootwearPage /></MainLayout>} />
              <Route path="/admin" element={<MainLayout><AdminDashboardPage /></MainLayout>} />
              <Route path="/cart" element={<MainLayout><ProductsPage /></MainLayout>} />
            </Routes>
            <ShannonChat />
          </WishlistProvider>
        </CartProvider>
        </GeoProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
